import { useEffect, useMemo, useRef, useState } from "react";

const LEVEL_LABEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];
const LEVEL_COLOR = ["#f87171", "#f87171", "#fbbf24", "#4F8CFF", "#34d399", "#34d399"];

function layout(nodes, edges, width, height) {
  const n = nodes.length;
  if (!n) return [];
  const idx = Object.fromEntries(nodes.map((d, i) => [d.id, i]));
  const pos = nodes.map((_, i) => {
    const a = (i / n) * Math.PI * 2;
    const r = Math.min(width, height) * 0.32;
    return { x: width / 2 + Math.cos(a) * r, y: height / 2 + Math.sin(a) * r, vx: 0, vy: 0 };
  });
  const links = edges.filter(e => idx[e.from] != null && idx[e.to] != null).map(e => [idx[e.from], idx[e.to]]);
  const k = Math.sqrt((width * height) / n) * 0.8;
  const degree = new Array(n).fill(0);
  for (const [a, b] of links) { degree[a]++; degree[b]++; }
  for (let it = 0; it < 320; it++) {
    const t = 1 - it / 320;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let dx = pos[i].x - pos[j].x, dy = pos[i].y - pos[j].y;
        let d2 = dx * dx + dy * dy || 0.01;
        const d = Math.sqrt(d2);
        const f = (k * k) / d2 * 1.6;
        dx = (dx / d) * f; dy = (dy / d) * f;
        pos[i].vx += dx; pos[i].vy += dy; pos[j].vx -= dx; pos[j].vy -= dy;
      }
    }
    for (const [a, b] of links) {
      let dx = pos[b].x - pos[a].x, dy = pos[b].y - pos[a].y;
      const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const f = ((d - k * 0.9) / d) * 0.05;
      dx *= f; dy *= f;
      pos[a].vx += dx; pos[a].vy += dy; pos[b].vx -= dx; pos[b].vy -= dy;
    }
    pos.forEach((p, i) => {
      const g = degree[i] === 0 ? 0.03 : 0.012;
      p.vx += (width / 2 - p.x) * g; p.vy += (height / 2 - p.y) * g;
      p.x += Math.max(-12, Math.min(12, p.vx * t)); p.y += Math.max(-12, Math.min(12, p.vy * t));
      p.vx *= 0.55; p.vy *= 0.55;
      p.x = Math.max(80, Math.min(width - 80, p.x)); p.y = Math.max(44, Math.min(height - 78, p.y));
    });
  }
  return pos.map(p => ({ x: p.x, y: p.y }));
}

export function KnowledgeMap({ nodes, edges, onSelect }) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 900, h: 560 });
  const [hover, setHover] = useState(null);
  const [drag, setDrag] = useState(null);
  const [override, setOverride] = useState({});

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: Math.max(480, Math.min(720, el.clientWidth * 0.62)) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const base = useMemo(() => layout(nodes, edges, size.w, size.h), [nodes, edges, size.w, size.h]);
  const pos = nodes.map((d, i) => override[d.id] || base[i]);
  const byId = Object.fromEntries(nodes.map((d, i) => [d.id, pos[i]]));
  const neighbours = useMemo(() => {
    const m = {};
    for (const e of edges) { (m[e.from] ||= new Set()).add(e.to); (m[e.to] ||= new Set()).add(e.from); }
    return m;
  }, [edges]);

  const onMove = (e) => {
    if (!drag) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setOverride(o => ({ ...o, [drag.id]: { x: e.clientX - rect.left, y: e.clientY - rect.top } }));
    setDrag(d => ({ ...d, moved: true }));
  };
  const onUp = (e) => {
    if (drag && !drag.moved) onSelect?.(drag.id);
    setDrag(null);
  };

  const radius = (d) => 14 + Math.min(14, (d.sources || 0) * 3);
  const dim = (id) => hover && hover !== id && !neighbours[hover]?.has(id);

  return (
    <div ref={wrapRef} className="relative w-full select-none aria-card overflow-hidden" data-testid="knowledge-map"
      onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={() => setDrag(null)}>
      <svg width={size.w} height={size.h} className="block">
        <defs>
          <filter id="kg-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <marker id="kg-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#4F8CFF" opacity="0.7" />
          </marker>
          <radialGradient id="kg-bg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#2563FF" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#030711" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width={size.w} height={size.h} fill="url(#kg-bg)" />
        {edges.map((e, i) => {
          const a = byId[e.from], b = byId[e.to];
          if (!a || !b) return null;
          const prereq = e.relation === "PREREQUISITE_OF";
          const lit = hover && (hover === e.from || hover === e.to);
          return (
            <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={lit ? "#4F8CFF" : prereq ? "#4F8CFF" : "#ffffff"}
              strokeOpacity={lit ? 0.9 : prereq ? 0.45 : hover ? 0.05 : 0.12}
              strokeWidth={lit ? 2 : prereq ? 1.5 : 1}
              strokeDasharray={prereq ? "" : "3 4"}
              markerEnd={prereq ? "url(#kg-arrow)" : undefined}
              className="transition-[stroke-opacity] duration-200" />
          );
        })}
        {nodes.map((d, i) => {
          const p = pos[i]; if (!p) return null;
          const c = LEVEL_COLOR[d.level] || LEVEL_COLOR[0];
          const r = radius(d);
          const faded = dim(d.id);
          return (
            <g key={d.id} transform={`translate(${p.x},${p.y})`} style={{ opacity: faded ? 0.25 : 1, transition: "opacity 200ms", cursor: "pointer" }}
              onPointerDown={(e) => { e.preventDefault(); setDrag({ id: d.id, moved: false }); }}
              onPointerEnter={() => setHover(d.id)} onPointerLeave={() => setHover(null)}
              data-testid={`map-node-${d.id}`}>
              <circle r={r + 8} fill={c} opacity={hover === d.id ? 0.25 : 0.12} className="kg-pulse" style={{ animationDelay: `${i * 0.13}s` }} />
              <circle r={r} fill="#060b1a" stroke={c} strokeWidth={hover === d.id ? 3 : 2} filter="url(#kg-glow)" />
              <circle r={Math.max(3, r * 0.35)} fill={c} opacity={0.9} />
              <text y={r + 15} textAnchor="middle" fontSize="11" fill={hover === d.id ? "#fff" : "#c7d2fe"} fontFamily="inherit" style={{ pointerEvents: "none" }}>
                {d.id.length > 22 ? d.id.slice(0, 21) + "…" : d.id}
              </text>
            </g>
          );
        })}
      </svg>
      {hover && byId[hover] && (() => {
        const d = nodes.find(n => n.id === hover);
        return (
          <div className="absolute pointer-events-none px-3 py-2 rounded-md bg-[#0b1226]/95 border border-white/10 text-xs shadow-xl backdrop-blur"
            style={{ left: Math.min(size.w - 200, byId[hover].x + 18), top: Math.max(8, byId[hover].y - 52) }} data-testid="map-tooltip">
            <div className="font-display text-sm mb-0.5">{d.id}</div>
            <div style={{ color: LEVEL_COLOR[d.level] }}>{LEVEL_LABEL[d.level]} · level {d.level}</div>
            <div className="text-[var(--aria-text-muted)]">{d.sources} source(s) · {neighbours[d.id]?.size || 0} link(s) · click to open</div>
          </div>
        );
      })()}
      <div className="absolute bottom-3 left-4 flex items-center gap-4 text-[11px] text-[var(--aria-text-muted)]" data-testid="map-legend">
        {[[1, "Weak"], [2, "Familiar"], [3, "Understands"], [4, "Strong"]].map(([l, t]) => (
          <span key={l} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: LEVEL_COLOR[l], boxShadow: `0 0 8px ${LEVEL_COLOR[l]}` }} />{t}</span>
        ))}
        <span className="flex items-center gap-1.5"><span className="w-5 border-t border-[#4F8CFF]" />prerequisite</span>
        <span className="flex items-center gap-1.5"><span className="w-5 border-t border-dashed border-white/40" />related</span>
      </div>
    </div>
  );
}
